import { useState } from 'react';
import { Search, Filter, X, SlidersHorizontal } from 'lucide-react';
import { ChatFolder } from '@/services/folderService';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { AdvancedSearchFilters, AdvancedFilters, getDefaultFilters } from './AdvancedSearchFilters';

interface SearchChatsInputProps {
  value: string;
  onChange: (value: string) => void;
  folders?: ChatFolder[];
  selectedFolderId?: string | null;
  onFolderChange?: (folderId: string | null) => void;
  advancedFilters?: AdvancedFilters;
  onAdvancedFiltersChange?: (filters: AdvancedFilters) => void;
}

export function SearchChatsInput({ 
  value, 
  onChange, 
  folders = [],
  selectedFolderId,
  onFolderChange,
  advancedFilters,
  onAdvancedFiltersChange,
}: SearchChatsInputProps) {
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const selectedFolder = folders.find(f => f.id === selectedFolderId);
  const hasFilter = selectedFolderId !== undefined && selectedFolderId !== null;

  const currentFilters = advancedFilters || getDefaultFilters();
  
  const hasAdvancedFilters = 
    currentFilters.datePreset !== 'any' ||
    currentFilters.folders.length > 0 ||
    currentFilters.searchInContent ||
    currentFilters.pinnedOnly;

  const activeFilterCount = [
    currentFilters.datePreset !== 'any',
    currentFilters.folders.length > 0,
    currentFilters.searchInContent,
    currentFilters.pinnedOnly,
  ].filter(Boolean).length;

  return (
    <div className="space-y-2">
      <div className="relative flex gap-1">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Buscar conversaciones..."
            className="w-full h-9 pl-9 pr-4 rounded-lg bg-muted border border-border text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all"
          />
        </div>
        
        {/* Quick folder filter */}
        {folders.length > 0 && onFolderChange && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant={hasFilter ? "secondary" : "outline"} 
                size="icon" 
                className="h-9 w-9 flex-shrink-0"
              >
                <Filter className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem 
                onClick={() => onFolderChange(null)}
                className={!hasFilter ? 'bg-accent' : ''}
              >
                📂 Todas las carpetas
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => onFolderChange('uncategorized')}
                className={selectedFolderId === 'uncategorized' ? 'bg-accent' : ''}
              >
                📥 Sin categoría
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {folders.map((folder) => (
                <DropdownMenuItem
                  key={folder.id}
                  onClick={() => onFolderChange(folder.id)}
                  className={selectedFolderId === folder.id ? 'bg-accent' : ''}
                >
                  <span className="mr-2">{folder.icon}</span>
                  <span style={{ color: folder.color }}>{folder.name}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Advanced filters */}
        {onAdvancedFiltersChange && (
          <Popover open={advancedOpen} onOpenChange={setAdvancedOpen}>
            <PopoverTrigger asChild>
              <Button 
                variant={hasAdvancedFilters ? "secondary" : "outline"} 
                size="icon" 
                className="h-9 w-9 flex-shrink-0 relative"
              >
                <SlidersHorizontal className="h-4 w-4" />
                {activeFilterCount > 0 && (
                  <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-primary text-primary-foreground text-[10px] flex items-center justify-center font-medium">
                    {activeFilterCount}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-0" align="end">
              <AdvancedSearchFilters
                folders={folders}
                filters={currentFilters}
                onFiltersChange={onAdvancedFiltersChange}
                onClose={() => setAdvancedOpen(false)}
              />
            </PopoverContent>
          </Popover>
        )}
      </div>

      {/* Active filter badge */}
      {hasFilter && (
        <div className="flex items-center gap-1">
          <span 
            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs bg-primary/10 text-primary"
          >
            {selectedFolderId === 'uncategorized' ? (
              <>📥 Sin categoría</>
            ) : selectedFolder ? (
              <>
                <span>{selectedFolder.icon}</span>
                <span style={{ color: selectedFolder.color }}>{selectedFolder.name}</span>
              </>
            ) : null}
            <button
              onClick={() => onFolderChange?.(null)}
              className="ml-0.5 hover:bg-primary/20 rounded-full p-0.5"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        </div>
      )}

      {/* Advanced filter badges */}
      {hasAdvancedFilters && (
        <div className="flex flex-wrap items-center gap-1">
          {currentFilters.datePreset !== 'any' && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-secondary/50 text-secondary-foreground">
              📅 {currentFilters.datePreset === 'week' ? 'Última semana' : 
                  currentFilters.datePreset === 'month' ? 'Último mes' : 
                  currentFilters.datePreset === '3months' ? '3 meses' : 'Personalizado'}
            </span>
          )}
          {currentFilters.searchInContent && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-secondary/50 text-secondary-foreground">
              📝 Contenido
            </span>
          )}
          {currentFilters.pinnedOnly && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-secondary/50 text-secondary-foreground">
              📌 Fijados
            </span>
          )}
          {currentFilters.folders.length > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-secondary/50 text-secondary-foreground">
              📁 {currentFilters.folders.length} carpeta{currentFilters.folders.length > 1 ? 's' : ''}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
